<script setup>
import { ref, computed } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import Button from 'dashboard/components-next/button/Button.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAcesso } from 'dashboard/composables/useAcesso';
import SeletorDePapel from './SeletorDePapel.vue';
import { corpoDoPapel } from './acessoDaPessoa';

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const agentName = ref('');
const agentEmail = ref('');
const selectedRoleId = ref('agent');
// `''` é "nenhum papel personalizado" — o valor que uma <option> vazia carrega.
const selectedCustomRoleId = ref('');

const rules = {
  agentName: { required },
  agentEmail: { required, email },
  selectedRoleId: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  agentEmail,
  selectedRoleId,
});

const uiFlags = useMapGetter('agents/getUIFlags');
const currentUserRole = useMapGetter('getCurrentRole');

// Só administrador atribui papel personalizado; para moderador o campo não aparece, porque campo
// que existe e sempre dá erro é pior que campo ausente.
const { isAdmin } = useAdmin();
const customRoles = useMapGetter('customRoles/getCustomRoles');
// ACESSO-01 (L8b): com a chave ACESSO_GRANULAR ligada, entregar papel personalizado e convidar administrador são do
// `roles:manage` (`agents_controller`, `Acesso::Concessao`: ninguém dá o que não tem, e o modelo Administrador é o
// único com ele); desligada, o papel, como abaixo.
const { decide, ligado } = useAcesso();
const entregaPapel = computed(() =>
  decide('roles:manage', () => isAdmin.value)
);
const convidaAdministrador = computed(() =>
  decide('roles:manage', () => currentUserRole.value !== 'moderator')
);

// ACESSO-01 (L9b): com a chave ligada, o convite leva o papel do select único (`SeletorDePapel`), que nasce no modelo
// Atendente, o `agent` de hoje. Desligada, o papel base e o papel personalizado, como hoje.
const papelEscolhido = ref('modelo:atendente');
const rotuloDoPapel = 'papel-do-convite';

const roles = computed(() => {
  const allRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'moderator',
      name: 'moderator',
      label: t('AGENT_MGMT.AGENT_TYPES.MODERATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  // A moderator can only invite peers and agents, never administrators.
  if (!convidaAdministrador.value) {
    return allRoles.filter(role => role.id !== 'administrator');
  }

  return allRoles;
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const addAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      name: agentName.value,
      email: agentEmail.value,
    };

    if (ligado.value) {
      Object.assign(payload, corpoDoPapel(papelEscolhido.value));
    } else {
      payload.role = selectedRole.value.name;
      if (entregaPapel.value && selectedCustomRoleId.value) {
        payload.custom_role_id = selectedCustomRoleId.value;
      }
    }

    await store.dispatch('agents/create', payload);
    useAlert(t('AGENT_MGMT.ADD.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    const {
      response: {
        data: {
          error: errorResponse = '',
          attributes: attributes = [],
          message: attrError = '',
        } = {},
      } = {},
    } = error;

    let errorMessage = '';
    if (error?.response?.status === 422 && !attributes.includes('base')) {
      errorMessage = t('AGENT_MGMT.ADD.API.EXIST_MESSAGE');
    } else {
      errorMessage = t('AGENT_MGMT.ADD.API.ERROR_MESSAGE');
    }
    useAlert(errorResponse || attrError || errorMessage);
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="$t('AGENT_MGMT.ADD.TITLE')"
      :header-content="$t('AGENT_MGMT.ADD.DESC')"
    />
    <form class="flex flex-col items-start w-full" @submit.prevent="addAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <template v-if="!ligado">
        <div class="w-full">
          <label :class="{ error: v$.selectedRoleId.$error }">
            {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.LABEL') }}
            <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
              <option v-for="role in roles" :key="role.id" :value="role.id">
                {{ role.label }}
              </option>
            </select>
            <span v-if="v$.selectedRoleId.$error" class="message">
              {{ $t('AGENT_MGMT.ADD.FORM.AGENT_TYPE.ERROR') }}
            </span>
          </label>
        </div>

        <div v-if="entregaPapel" class="w-full">
          <label>
            {{ $t('AGENT_MGMT.EDIT.FORM.CUSTOM_ROLE.LABEL') }}
            <select v-model="selectedCustomRoleId">
              <option value="">
                {{ $t('AGENT_MGMT.EDIT.FORM.CUSTOM_ROLE.NONE') }}
              </option>
              <option
                v-for="role in customRoles"
                :key="role.id"
                :value="role.id"
              >
                {{ role.name }}
              </option>
            </select>
            <p class="text-body-main text-n-slate-11">
              {{ $t('AGENT_MGMT.EDIT.FORM.CUSTOM_ROLE.HELP') }}
            </p>
          </label>
        </div>
      </template>

      <div v-else class="flex flex-col w-full mb-4" data-papel>
        <span
          :id="rotuloDoPapel"
          class="text-sm font-medium leading-7 text-n-slate-12"
        >
          {{ $t('AGENT_MGMT.ACESSO.PAPEL.LABEL') }}
        </span>
        <SeletorDePapel
          v-model="papelEscolhido"
          :aria-labelledby="rotuloDoPapel"
        />
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentEmail.$error }">
          {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
          <input
            v-model="agentEmail"
            type="email"
            :placeholder="$t('AGENT_MGMT.ADD.FORM.EMAIL.PLACEHOLDER')"
            @input="v$.agentEmail.$touch"
          />
        </label>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <Button
          faded
          slate
          type="reset"
          :label="$t('AGENT_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="emit('close')"
        />
        <Button
          type="submit"
          :label="$t('AGENT_MGMT.ADD.FORM.SUBMIT')"
          :disabled="v$.$invalid || uiFlags.isCreating"
          :is-loading="uiFlags.isCreating"
        />
      </div>
    </form>
  </div>
</template>
